import './PolicyStyles.css';
import { exportToWord } from '../utils/exportToWord';

const PregnancyPolicy = ({
  clientName,
  companyNumber,
  clientAddress,
  effectiveDate,
  hrContact,
  transportManagerContact,
}) => {
  const handleExportToWord = () => {
    const policyContent = document.getElementById('policy-content');
    exportToWord(policyContent.innerHTML, "Pregnancy_Policy.docx");
  };

  return (
    <div className="policy-container" id="policy-content">
      <h3 className="policy-title">Pregnancy Policy</h3>

      <p className="policy-details"><strong>Company Name:</strong> {clientName || "[Insert Company Name]"}</p>
      <p className="policy-details"><strong>Company Number:</strong> {companyNumber || "[Insert Company Number]"}</p>
      <p className="policy-details"><strong>Company Address:</strong> {clientAddress || "[Insert Company Address]"}</p>
      <p className="policy-details"><strong>Policy Effective Date:</strong> {effectiveDate || "[Insert Effective Date]"}</p>

      <h4 className="section-title">1. Purpose</h4>
      <p className="section-content">
        {clientName || "The Company"} is committed to supporting employees during pregnancy and ensuring the health, safety, and wellbeing of
        expectant mothers in our transport operations (HGV/PSV/Van) and office functions. This policy sets out how we will assess risks, provide
        reasonable adjustments, and signpost leave and pay arrangements (with maternity leave and pay detailed in the separate Maternity Policy).
      </p>

      <h4 className="section-title">2. Scope</h4>
      <p className="section-content">
        This policy applies to all employees, including drivers, office, workshop and yard staff, whether permanent, temporary or agency. It covers
        pregnancy and new mothers up to at least six months after birth and/or for as long as the employee is breastfeeding.
      </p>

      <h4 className="section-title">3. Pregnancy Notification & Communication</h4>
      <p className="section-content">
        Employees are encouraged to inform their line manager or HR of pregnancy as early as they feel comfortable so that a risk assessment and
        appropriate support can be arranged. The Company will keep health information confidential and treat employees with dignity and respect at all times.
        Managers will maintain regular, sensitive communication and confirm any agreed adjustments in writing.
      </p>

      <h4 className="section-title">4. Risk Assessment & Health and Safety</h4>
      <p className="section-content">
        On notification of pregnancy, the Company will conduct a specific risk assessment and review it regularly. Typical transport-related risks may include:
      </p>
      <ul>
        <li>Prolonged driving, whole-body vibration, uneven surfaces, slips and trips at depots and delivery points.</li>
        <li>Manual handling (loading, securing, luggage) and awkward postures in cabs or confined spaces.</li>
        <li>Night work, lone working, remote locations, extreme temperatures, chemicals, fuels or exhaust exposure.</li>
        <li>Fatigue, dehydration, restricted toilet access, and limited opportunities for rest breaks.</li>
      </ul>
      <p className="section-content">
        Reasonable adjustments may include: more frequent rest and comfort breaks; alternative or shorter routes/shifts; assistance with manual handling;
        temporarily reallocating safety‑critical tasks; additional parking near entrances; access to drinking water and facilities; suitable seating and
        cab adjustments (including safe use of seatbelts). Where risks cannot be removed or suitable alternative work provided, the employee may be
        suspended on health and safety grounds on appropriate terms.
      </p>

      <h4 className="section-title">5. Antenatal Care & Time Off</h4>
      <p className="section-content">
        Pregnant employees are entitled to reasonable paid time off to attend antenatal appointments recommended by a registered medical practitioner, midwife
        or health visitor. Managers will schedule work accordingly. Evidence of appointments may be requested after the first appointment. Partners may have
        time off rights as per the Company’s Family Leave/Maternity & Paternity policies.
      </p>

      <h4 className="section-title">6. Maternity Leave & Pay</h4>
      <p className="section-content">
        Entitlement to maternity leave and pay, including eligibility, notice requirements and Keeping in Touch arrangements, is set out in the Company’s
        <strong> Maternity Policy</strong>. This Pregnancy Policy should be read alongside that policy.
      </p>

      <h4 className="section-title">7. Uniform, PPE & Facilities</h4>
      <p className="section-content">
        The Company will provide suitable maternity uniform and PPE as required for the role (e.g., adjustable hi‑viz, appropriate footwear). Facilities for
        rest, hydration and toilet access will be made available. Managers will ensure depot/site access arrangements are practical for expectant mothers.
      </p>

      <h4 className="section-title">8. Adjustments, Alternative Duties & Suspension</h4>
      <p className="section-content">
        Where the risk assessment identifies hazards that cannot be adequately controlled, the Company will consider temporary adjustments, offering suitable
        alternative duties where available. If neither is possible, the employee may be suspended on health and safety grounds on appropriate terms in line
        with legislation and Company policy.
      </p>

      <h4 className="section-title">9. Data Protection & Confidentiality</h4>
      <p className="section-content">
        Personal health information will be handled confidentially and processed in accordance with UK GDPR. Details will only be shared with those who need
        to know to implement adjustments and ensure safety.
      </p>

      <h4 className="section-title">10. Return to Work & Breastfeeding Support</h4>
      <p className="section-content">
        Prior to return, the risk assessment will be reviewed and any ongoing adjustments agreed. On request, the Company will make reasonable arrangements
        for expressing and storing breast milk and for appropriate rest breaks. Employees may also request flexible working in line with the Flexible Working
        Policy, and should refer to the Maternity Policy for Keeping in Touch days and other arrangements.
      </p>

      <h4 className="section-title">Contact Information</h4>
      <ul>
        <li><strong>Human Resources:</strong> {hrContact || "[Insert Contact Number]"}</li>
        <li><strong>Transport Manager:</strong> {transportManagerContact || "[Insert Contact Number]"}</li>
      </ul>

      <h4 className="section-title">Pregnancy Risk Assessment — Employee and Manager Signatures</h4>
      <p>
        I confirm that a pregnancy risk assessment has been discussed and appropriate adjustments agreed as required. I understand that this assessment
        will be reviewed regularly and I will notify the Company if my circumstances change.
      </p>

      <div className="signature-section" style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2rem' }}>
        <div style={{ width: '45%' }}>
          <p><strong>Employee Signature:</strong> _________________________</p>
          <p><strong>Date:</strong> _________________________</p>
        </div>
        <div style={{ width: '45%' }}>
          <p><strong>Manager Signature:</strong> _________________________</p>
          <p><strong>Date:</strong> _________________________</p>
        </div>
      </div>

      <h4 className="section-title">Employee Acknowledgment and Signature</h4>
      <p>I confirm that I have read and understood the Pregnancy Policy and agree to its terms.</p>
      <p>Employee Name: _________________________</p>
      <p>Employee Signature: _________________________</p>
      <p>Date: _________________________</p>

      <h4 className="section-title">Management Approval and Signature</h4>
      <p>This policy has been approved by senior management and is now in effect.</p>
      <p>Manager/Officer Name: _________________________</p>
      <p>Title: _________________________</p>
      <p>Signature: _________________________</p>
      <p>Date: _________________________</p>

      <div className="mt-4 text-center">
        <button
          onClick={handleExportToWord}
          className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 transition"
        >
          Export to Word
        </button>
      </div>
    </div>
  );
};

export default PregnancyPolicy;
